import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useRBAC } from "../contexts/RBACContext";
import Navbar from "../components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, BookOpen, XCircle, Ticket, Users, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Filter, X, Calendar, Download } from "lucide-react";
import { toast } from "sonner";

export default function KnowledgeBase({ user, logout, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const { hasPermission } = useRBAC();
  const [closedTickets, setClosedTickets] = useState([]);
  const [totalClosedTickets, setTotalClosedTickets] = useState(0);
  const [closedHRRequests, setClosedHRRequests] = useState([]);
  const [totalClosedHR, setTotalClosedHR] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("it");
  const [currentPage, setCurrentPage] = useState(1);
  const [hrCurrentPage, setHrCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  const navigate = useNavigate();

  // IT Filter state
  const [filterCategory, setFilterCategory] = useState("");
  const [filterDealership, setFilterDealership] = useState("");
  const [filterAssignedTo, setFilterAssignedTo] = useState("");
  const [filterSubmittedBy, setFilterSubmittedBy] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // IT Sort state
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // IT Filter options
  const [categories, setCategories] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [creators, setCreators] = useState([]);

  // HR Filter state
  const [hrFilterRequestType, setHrFilterRequestType] = useState("");
  const [hrFilterDealership, setHrFilterDealership] = useState("");
  const [hrFilterAssignedTo, setHrFilterAssignedTo] = useState("");
  const [hrFilterSubmittedBy, setHrFilterSubmittedBy] = useState("");
  const [hrFilterDateFrom, setHrFilterDateFrom] = useState("");
  const [hrFilterDateTo, setHrFilterDateTo] = useState("");
  const [hrShowFilters, setHrShowFilters] = useState(false);

  // HR Sort state
  const [hrSortBy, setHrSortBy] = useState("created_at");
  const [hrSortOrder, setHrSortOrder] = useState("desc");

  // HR Filter options
  const [hrRequestTypes, setHrRequestTypes] = useState([]);
  const [hrDealerships, setHrDealerships] = useState([]);
  const [hrAssignees, setHrAssignees] = useState([]);
  const [hrCreators, setHrCreators] = useState([]);

  const effectiveRole = previewRole || user.role;
  const effectiveDepartment = previewDepartment || user.department;
  const canViewHR = hasPermission('hr_view', effectiveRole, effectiveDepartment);

  // Fetch IT filter options once
  useEffect(() => {
    const fetchITFilterOptions = async () => {
      try {
        const [catRes, dealRes, assignRes, creatorRes] = await Promise.all([
          axios.get(`${API}/tickets/categories`),
          axios.get(`${API}/tickets/dealerships`),
          axios.get(`${API}/tickets/assignees`),
          axios.get(`${API}/tickets/creators`),
        ]);
        setCategories(catRes.data);
        setDealerships(dealRes.data);
        setAssignees(assignRes.data);
        setCreators(creatorRes.data);
      } catch (err) { console.error("Non-critical error:", err.message); }
    };
    fetchITFilterOptions();
  }, []);

  // Fetch HR filter options once
  useEffect(() => {
    if (!canViewHR) return;
    const fetchHRFilterOptions = async () => {
      try {
        const [typesRes, dealRes, assignRes, creatorRes] = await Promise.all([
          axios.get(`${API}/hr-requests/request-types`),
          axios.get(`${API}/hr-requests/dealerships`),
          axios.get(`${API}/hr-requests/assignees`),
          axios.get(`${API}/hr-requests/creators`),
        ]);
        setHrRequestTypes(typesRes.data);
        setHrDealerships(dealRes.data);
        setHrAssignees(assignRes.data);
        setHrCreators(creatorRes.data);
      } catch (err) { console.error("Non-critical error:", err.message); }
    };
    fetchHRFilterOptions();
  }, [canViewHR]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
      setHrCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch IT tickets
  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("status", "closed");
      params.set("skip", String((currentPage - 1) * ITEMS_PER_PAGE));
      params.set("limit", String(ITEMS_PER_PAGE));
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (filterCategory) params.set("category", filterCategory);
      if (filterDealership) params.set("dealership", filterDealership);
      if (filterAssignedTo) params.set("assigned_to", filterAssignedTo);
      if (filterSubmittedBy) params.set("created_by_name", filterSubmittedBy);
      if (filterDateFrom) params.set("date_from", filterDateFrom);
      if (filterDateTo) params.set("date_to", filterDateTo);
      if (sortBy) params.set("sort_by", sortBy);
      if (sortOrder) params.set("sort_order", sortOrder);

      const response = await axios.get(`${API}/tickets?${params.toString()}`);
      setClosedTickets(response.data.tickets);
      setTotalClosedTickets(response.data.total);
    } catch (error) {
      toast.error("Failed to load ticket history");
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, filterCategory, filterDealership, filterAssignedTo, filterSubmittedBy, filterDateFrom, filterDateTo, sortBy, sortOrder]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Fetch HR requests (server-side paginated)
  const fetchHRRequests = useCallback(async () => {
    if (!canViewHR) return;
    try {
      const params = new URLSearchParams();
      params.set("status", "closed");
      params.set("skip", String((hrCurrentPage - 1) * ITEMS_PER_PAGE));
      params.set("limit", String(ITEMS_PER_PAGE));
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (hrFilterRequestType) params.set("request_type", hrFilterRequestType);
      if (hrFilterDealership) params.set("dealership", hrFilterDealership);
      if (hrFilterAssignedTo) params.set("assigned_to", hrFilterAssignedTo);
      if (hrFilterSubmittedBy) params.set("created_by_name", hrFilterSubmittedBy);
      if (hrFilterDateFrom) params.set("date_from", hrFilterDateFrom);
      if (hrFilterDateTo) params.set("date_to", hrFilterDateTo);
      if (hrSortBy) params.set("sort_by", hrSortBy);
      if (hrSortOrder) params.set("sort_order", hrSortOrder);

      const response = await axios.get(`${API}/hr-requests?${params.toString()}`);
      setClosedHRRequests(response.data.requests);
      setTotalClosedHR(response.data.total);
    } catch (err) { console.error("Non-critical error:", err.message); }
  }, [canViewHR, hrCurrentPage, debouncedSearch, hrFilterRequestType, hrFilterDealership, hrFilterAssignedTo, hrFilterSubmittedBy, hrFilterDateFrom, hrFilterDateTo, hrSortBy, hrSortOrder]);

  useEffect(() => {
    fetchHRRequests();
  }, [fetchHRRequests]);

  // Pagination calculations
  const totalPages = Math.ceil(totalClosedTickets / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalClosedTickets);

  const hrTotalPages = Math.ceil(totalClosedHR / ITEMS_PER_PAGE);
  const hrStartIndex = (hrCurrentPage - 1) * ITEMS_PER_PAGE;
  const hrEndIndex = Math.min(hrStartIndex + ITEMS_PER_PAGE, totalClosedHR);

  // IT sort handler
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  // HR sort handler
  const handleHRSort = (field) => {
    if (hrSortBy === field) {
      setHrSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setHrSortBy(field);
      setHrSortOrder("asc");
    }
    setHrCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterCategory("");
    setFilterDealership("");
    setFilterAssignedTo("");
    setFilterSubmittedBy("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setCurrentPage(1);
  };

  const clearHRFilters = () => {
    setHrFilterRequestType("");
    setHrFilterDealership("");
    setHrFilterAssignedTo("");
    setHrFilterSubmittedBy("");
    setHrFilterDateFrom("");
    setHrFilterDateTo("");
    setHrCurrentPage(1);
  };

  const activeFilterCount = [filterCategory, filterDealership, filterAssignedTo, filterSubmittedBy, filterDateFrom, filterDateTo].filter(Boolean).length;
  const hrActiveFilterCount = [hrFilterRequestType, hrFilterDealership, hrFilterAssignedTo, hrFilterSubmittedBy, hrFilterDateFrom, hrFilterDateTo].filter(Boolean).length;

  const [exporting, setExporting] = useState(false);

  const exportITTicketsCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set("status", "closed");
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (filterCategory) params.set("category", filterCategory);
      if (filterDealership) params.set("dealership", filterDealership);
      if (filterAssignedTo) params.set("assigned_to", filterAssignedTo);
      if (filterSubmittedBy) params.set("created_by_name", filterSubmittedBy);
      if (filterDateFrom) params.set("date_from", filterDateFrom);
      if (filterDateTo) params.set("date_to", filterDateTo);
      if (sortBy) params.set("sort_by", sortBy);
      if (sortOrder) params.set("sort_order", sortOrder);

      const response = await axios.get(`${API}/tickets/export-csv?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers["content-disposition"];
      const filename = disposition ? disposition.split("filename=")[1] : "ticket_history.csv";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${totalClosedTickets} ticket${totalClosedTickets !== 1 ? "s" : ""} to CSV`);
    } catch (err) {
      toast.error("Failed to export tickets");
    } finally {
      setExporting(false);
    }
  };

  const exportHRRequestsCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set("status", "closed");
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (hrFilterRequestType) params.set("request_type", hrFilterRequestType);
      if (hrFilterDealership) params.set("dealership", hrFilterDealership);
      if (hrFilterAssignedTo) params.set("assigned_to", hrFilterAssignedTo);
      if (hrFilterSubmittedBy) params.set("created_by_name", hrFilterSubmittedBy);
      if (hrFilterDateFrom) params.set("date_from", hrFilterDateFrom);
      if (hrFilterDateTo) params.set("date_to", hrFilterDateTo);
      if (hrSortBy) params.set("sort_by", hrSortBy);
      if (hrSortOrder) params.set("sort_order", hrSortOrder);

      const response = await axios.get(`${API}/hr-requests/export-csv?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers["content-disposition"];
      const filename = disposition ? disposition.split("filename=")[1] : "hr_request_history.csv";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${totalClosedHR} HR request${totalClosedHR !== 1 ? "s" : ""} to CSV`);
    } catch (err) {
      toast.error("Failed to export HR requests");
    } finally {
      setExporting(false);
    }
  };

  const getPriorityBadgeClass = (priority) => {
    const classes = {
      low: "bg-green-100 text-green-700",
      medium: "bg-yellow-100 text-yellow-700",
      high: "bg-orange-100 text-orange-700",
      critical: "bg-red-100 text-red-700"
    };
    return classes[priority] || classes.medium;
  };

  const getRequestTypeBadgeClass = (type) => {
    const classes = {
      new: "bg-green-100 text-green-700",
      term: "bg-red-100 text-red-700",
      change: "bg-blue-100 text-blue-700",
      transfer: "bg-purple-100 text-purple-700",
      leave: "bg-yellow-100 text-yellow-700"
    };
    return classes[type] || "bg-gray-100 text-gray-700";
  };

  const getRequestTypeLabel = (type) => {
    const labels = { new: "New Hire", term: "Termination", change: "Change", transfer: "Transfer", leave: "Leave" };
    return labels[type] || type;
  };

  const SortIcon = ({ field, activeSort }) => {
    if (activeSort !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return activeSort === field
      ? (field === sortBy ? sortOrder : hrSortOrder) === "asc"
        ? <ChevronUp className="w-3 h-3 text-blue-600" />
        : <ChevronDown className="w-3 h-3 text-blue-600" />
      : <ChevronDown className="w-3 h-3 opacity-30" />;
  };

  const ITSortIcon = ({ field }) => {
    if (sortBy !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortOrder === "asc"
      ? <ChevronUp className="w-3 h-3 text-blue-600" />
      : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  const HRSortIcon = ({ field }) => {
    if (hrSortBy !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return hrSortOrder === "asc"
      ? <ChevronUp className="w-3 h-3 text-[#7a1d42]" />
      : <ChevronDown className="w-3 h-3 text-[#7a1d42]" />;
  };

  // Pagination component
  const PaginationControls = ({ current, total, onPageChange, testIdPrefix }) => {
    if (total <= 1) return null;
    return (
      <div className="flex items-center justify-between mt-4 px-2" data-testid={`${testIdPrefix}-pagination-controls`}>
        <div className="text-sm text-slate-600 dark:text-gray-400">
          Page {current} of {total}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onPageChange(Math.max(1, current - 1))} disabled={current === 1} data-testid={`${testIdPrefix}-prev-page-btn`}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, total) }, (_, i) => {
              let pageNum;
              if (total <= 5) pageNum = i + 1;
              else if (current <= 3) pageNum = i + 1;
              else if (current >= total - 2) pageNum = total - 4 + i;
              else pageNum = current - 2 + i;
              return (
                <Button key={pageNum} variant={current === pageNum ? "default" : "outline"} size="sm" className="w-8 h-8 p-0" onClick={() => onPageChange(pageNum)} data-testid={`${testIdPrefix}-page-${pageNum}-btn`}>
                  {pageNum}
                </Button>
              );
            })}
          </div>
          <Button variant="outline" size="sm" onClick={() => onPageChange(Math.min(total, current + 1))} disabled={current === total} data-testid={`${testIdPrefix}-next-page-btn`}>
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
        <Navbar user={user} logout={logout} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-slate-600 dark:text-white">Loading ticket history...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
      <Navbar user={user} logout={logout} effectiveRole={effectiveRole} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-slate-800 dark:text-white" data-testid="ticket-history-title">
              Ticket History
            </h1>
          </div>
          <p className="text-slate-600 dark:text-white">Browse all closed tickets and HR requests</p>
        </div>

        {/* View Toggle */}
        {canViewHR && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-slate-500 dark:text-slate-400 mr-2">View:</span>
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <button
                onClick={() => setViewMode("it")}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  viewMode === "it"
                    ? "bg-blue-600 text-white"
                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
                }`}
                data-testid="history-view-it"
              >
                <Ticket className="w-4 h-4" />
                IT Tickets ({totalClosedTickets})
              </button>
              <button
                onClick={() => setViewMode("hr")}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  viewMode === "hr"
                    ? "bg-[#7a1d42] text-white"
                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
                }`}
                data-testid="history-view-hr"
              >
                <Users className="w-4 h-4" />
                HR Requests ({totalClosedHR})
              </button>
            </div>
          </div>
        )}

        {/* ====== IT TICKETS TAB ====== */}
        {viewMode === "it" && (
          <>
            {/* Search + Filter Toggle */}
            <Card className="mb-4 border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-white w-5 h-5" />
                    <Input
                      type="text"
                      placeholder="Search closed tickets..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-12 h-11"
                      data-testid="ticket-history-search"
                    />
                  </div>
                  <Button
                    variant={showFilters ? "default" : "outline"}
                    onClick={() => setShowFilters(!showFilters)}
                    className="h-11 gap-2"
                    data-testid="toggle-filters-btn"
                  >
                    <Filter className="w-4 h-4" />
                    Filters
                    {activeFilterCount > 0 && (
                      <Badge className="bg-blue-500 text-white text-xs ml-1 px-1.5 py-0">{activeFilterCount}</Badge>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={exportITTicketsCSV}
                    disabled={exporting || totalClosedTickets === 0}
                    className="h-11 gap-2"
                    data-testid="export-tickets-csv-btn"
                  >
                    <Download className="w-4 h-4" />
                    {exporting ? "Exporting..." : "Export CSV"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* IT Filter Panel */}
            {showFilters && (
              <Card className="mb-4 border-0 shadow-md" data-testid="filter-panel">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-white">Filter Tickets</h3>
                    {activeFilterCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs gap-1" data-testid="clear-filters-btn">
                        <X className="w-3 h-3" /> Clear All
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Category</label>
                      <select value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }} className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 text-slate-700 dark:text-white" data-testid="filter-category">
                        <option value="">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Dealership</label>
                      <select value={filterDealership} onChange={(e) => { setFilterDealership(e.target.value); setCurrentPage(1); }} className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 text-slate-700 dark:text-white" data-testid="filter-dealership">
                        <option value="">All Dealerships</option>
                        {dealerships.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Assigned To</label>
                      <select value={filterAssignedTo} onChange={(e) => { setFilterAssignedTo(e.target.value); setCurrentPage(1); }} className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 text-slate-700 dark:text-white" data-testid="filter-assigned-to">
                        <option value="">All Assignees</option>
                        {assignees.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Submitted By</label>
                      <select value={filterSubmittedBy} onChange={(e) => { setFilterSubmittedBy(e.target.value); setCurrentPage(1); }} className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 text-slate-700 dark:text-white" data-testid="filter-submitted-by">
                        <option value="">All Submitters</option>
                        {creators.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1"><Calendar className="w-3 h-3 inline mr-1" />From Date</label>
                      <Input type="date" value={filterDateFrom} onChange={(e) => { setFilterDateFrom(e.target.value); setCurrentPage(1); }} className="h-9 text-sm" data-testid="filter-date-from" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1"><Calendar className="w-3 h-3 inline mr-1" />To Date</label>
                      <Input type="date" value={filterDateTo} onChange={(e) => { setFilterDateTo(e.target.value); setCurrentPage(1); }} className="h-9 text-sm" data-testid="filter-date-to" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* IT Tickets Table */}
            <div>
              {closedTickets.length === 0 ? (
                <Card className="border-0 shadow-md">
                  <CardContent className="p-12 text-center">
                    <XCircle className="w-16 h-16 text-slate-300 dark:text-white mx-auto mb-4" />
                    <p className="text-lg text-slate-600 dark:text-white">
                      {debouncedSearch || activeFilterCount > 0 ? "No results found" : "No closed tickets yet"}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-white mt-2">
                      {debouncedSearch || activeFilterCount > 0 ? "Try different search terms or filters" : "Closed tickets will appear here"}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="text-sm text-slate-600 dark:text-white mb-3">
                    Showing {startIndex + 1}-{endIndex} of {totalClosedTickets} closed {totalClosedTickets === 1 ? "ticket" : "tickets"}
                  </div>
                  <Card className="border-0 shadow-md overflow-hidden mb-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="history-tickets-table">
                        <thead>
                          <tr className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleSort("ticket_number")} className="flex items-center gap-1 hover:text-blue-600" data-testid="sort-ticket-number">Ticket # <ITSortIcon field="ticket_number" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">Title</th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleSort("category")} className="flex items-center gap-1 hover:text-blue-600" data-testid="sort-category">Category <ITSortIcon field="category" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">Priority</th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleSort("dealership")} className="flex items-center gap-1 hover:text-blue-600" data-testid="sort-dealership">Dealership <ITSortIcon field="dealership" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleSort("created_by_name")} className="flex items-center gap-1 hover:text-blue-600" data-testid="sort-submitted-by">Submitted By <ITSortIcon field="created_by_name" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleSort("assigned_to_name")} className="flex items-center gap-1 hover:text-blue-600" data-testid="sort-assigned-to">Assigned To <ITSortIcon field="assigned_to_name" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleSort("created_at")} className="flex items-center gap-1 hover:text-blue-600" data-testid="sort-date">Date <ITSortIcon field="created_at" /></button>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {closedTickets.map((ticket) => (
                            <tr key={ticket.id} onClick={() => navigate(`/tickets/${ticket.id}`)} className="border-b border-slate-100 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer transition-colors" data-testid={`history-ticket-${ticket.id}`}>
                              <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">{ticket.ticket_number}</td>
                              <td className="px-4 py-3 text-slate-800 dark:text-white max-w-[220px] truncate" title={ticket.title}>{ticket.title}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {ticket.category ? <Badge variant="outline" className="text-xs">{ticket.category}</Badge> : <span className="text-slate-400 text-xs">—</span>}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap"><Badge className={`text-xs ${getPriorityBadgeClass(ticket.priority)}`}>{ticket.priority}</Badge></td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs whitespace-nowrap">{ticket.dealership || "—"}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs whitespace-nowrap" data-testid={`ticket-submitted-by-${ticket.id}`}>{ticket.created_by_name || "—"}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs whitespace-nowrap" data-testid={`ticket-assigned-to-${ticket.id}`}>{ticket.assigned_to_name || "Unassigned"}</td>
                              <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">{new Date(ticket.created_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                  <PaginationControls current={currentPage} total={totalPages} onPageChange={setCurrentPage} testIdPrefix="history" />
                </>
              )}
            </div>
          </>
        )}

        {/* ====== HR REQUESTS TAB ====== */}
        {viewMode === "hr" && canViewHR && (
          <>
            {/* Search + Filter Toggle */}
            <Card className="mb-4 border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-white w-5 h-5" />
                    <Input
                      type="text"
                      placeholder="Search closed HR requests..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-12 h-11"
                      data-testid="hr-history-search"
                    />
                  </div>
                  <Button
                    variant={hrShowFilters ? "default" : "outline"}
                    onClick={() => setHrShowFilters(!hrShowFilters)}
                    className="h-11 gap-2"
                    data-testid="hr-toggle-filters-btn"
                  >
                    <Filter className="w-4 h-4" />
                    Filters
                    {hrActiveFilterCount > 0 && (
                      <Badge className="bg-[#7a1d42] text-white text-xs ml-1 px-1.5 py-0">{hrActiveFilterCount}</Badge>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={exportHRRequestsCSV}
                    disabled={exporting || totalClosedHR === 0}
                    className="h-11 gap-2"
                    data-testid="export-hr-csv-btn"
                  >
                    <Download className="w-4 h-4" />
                    {exporting ? "Exporting..." : "Export CSV"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* HR Filter Panel */}
            {hrShowFilters && (
              <Card className="mb-4 border-0 shadow-md" data-testid="hr-filter-panel">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-white">Filter HR Requests</h3>
                    {hrActiveFilterCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearHRFilters} className="text-xs gap-1" data-testid="hr-clear-filters-btn">
                        <X className="w-3 h-3" /> Clear All
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Request Type</label>
                      <select value={hrFilterRequestType} onChange={(e) => { setHrFilterRequestType(e.target.value); setHrCurrentPage(1); }} className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 text-slate-700 dark:text-white" data-testid="hr-filter-request-type">
                        <option value="">All Types</option>
                        {hrRequestTypes.map(t => <option key={t} value={t}>{getRequestTypeLabel(t)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Dealership</label>
                      <select value={hrFilterDealership} onChange={(e) => { setHrFilterDealership(e.target.value); setHrCurrentPage(1); }} className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 text-slate-700 dark:text-white" data-testid="hr-filter-dealership">
                        <option value="">All Dealerships</option>
                        {hrDealerships.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Submitted By</label>
                      <select value={hrFilterSubmittedBy} onChange={(e) => { setHrFilterSubmittedBy(e.target.value); setHrCurrentPage(1); }} className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 text-slate-700 dark:text-white" data-testid="hr-filter-submitted-by">
                        <option value="">All Submitters</option>
                        {hrCreators.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Assigned To</label>
                      <select value={hrFilterAssignedTo} onChange={(e) => { setHrFilterAssignedTo(e.target.value); setHrCurrentPage(1); }} className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 text-slate-700 dark:text-white" data-testid="hr-filter-assigned-to">
                        <option value="">All Assignees</option>
                        {hrAssignees.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1"><Calendar className="w-3 h-3 inline mr-1" />From Date</label>
                      <Input type="date" value={hrFilterDateFrom} onChange={(e) => { setHrFilterDateFrom(e.target.value); setHrCurrentPage(1); }} className="h-9 text-sm" data-testid="hr-filter-date-from" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1"><Calendar className="w-3 h-3 inline mr-1" />To Date</label>
                      <Input type="date" value={hrFilterDateTo} onChange={(e) => { setHrFilterDateTo(e.target.value); setHrCurrentPage(1); }} className="h-9 text-sm" data-testid="hr-filter-date-to" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* HR Requests Table */}
            <div>
              {closedHRRequests.length === 0 ? (
                <Card className="border-0 shadow-md">
                  <CardContent className="p-12 text-center">
                    <XCircle className="w-16 h-16 text-slate-300 dark:text-white mx-auto mb-4" />
                    <p className="text-lg text-slate-600 dark:text-white">
                      {debouncedSearch || hrActiveFilterCount > 0 ? "No results found" : "No closed HR requests yet"}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-white mt-2">
                      {debouncedSearch || hrActiveFilterCount > 0 ? "Try different search terms or filters" : "Closed HR requests will appear here"}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="text-sm text-slate-600 dark:text-white mb-3">
                    Showing {hrStartIndex + 1}-{hrEndIndex} of {totalClosedHR} closed HR {totalClosedHR === 1 ? "request" : "requests"}
                  </div>
                  <Card className="border-0 shadow-md overflow-hidden mb-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="history-hr-table">
                        <thead>
                          <tr className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleHRSort("request_number")} className="flex items-center gap-1 hover:text-[#7a1d42]" data-testid="hr-sort-request-number">Request # <HRSortIcon field="request_number" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleHRSort("employee_last_name")} className="flex items-center gap-1 hover:text-[#7a1d42]" data-testid="hr-sort-employee">Employee <HRSortIcon field="employee_last_name" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleHRSort("request_type")} className="flex items-center gap-1 hover:text-[#7a1d42]" data-testid="hr-sort-request-type">Type <HRSortIcon field="request_type" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">Job Title</th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleHRSort("dealership")} className="flex items-center gap-1 hover:text-[#7a1d42]" data-testid="hr-sort-dealership">Dealership <HRSortIcon field="dealership" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleHRSort("created_by_name")} className="flex items-center gap-1 hover:text-[#7a1d42]" data-testid="hr-sort-submitted-by">Submitted By <HRSortIcon field="created_by_name" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleHRSort("assigned_to_name")} className="flex items-center gap-1 hover:text-[#7a1d42]" data-testid="hr-sort-assigned-to">Assigned To <HRSortIcon field="assigned_to_name" /></button>
                            </th>
                            <th className="text-left px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                              <button onClick={() => handleHRSort("created_at")} className="flex items-center gap-1 hover:text-[#7a1d42]" data-testid="hr-sort-date">Date <HRSortIcon field="created_at" /></button>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {closedHRRequests.map((request) => (
                            <tr key={request.id} onClick={() => navigate(`/hr-requests/${request.id}`)} className="border-b border-slate-100 dark:border-slate-700 hover:bg-pink-50 dark:hover:bg-slate-700 cursor-pointer transition-colors" data-testid={`history-hr-${request.id}`}>
                              <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">{request.request_number || "N/A"}</td>
                              <td className="px-4 py-3 text-slate-800 dark:text-white whitespace-nowrap">
                                {request.employee_first_name} {request.employee_last_name}{request.employee_suffix ? ` ${request.employee_suffix}` : ""}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap"><Badge className={`text-xs ${getRequestTypeBadgeClass(request.request_type)}`}>{getRequestTypeLabel(request.request_type)}</Badge></td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs whitespace-nowrap">{request.job_title || "—"}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs whitespace-nowrap">{request.dealership || "—"}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs whitespace-nowrap" data-testid={`hr-submitted-by-${request.id}`}>{request.created_by_name || "—"}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs whitespace-nowrap" data-testid={`hr-assigned-to-${request.id}`}>{request.assigned_to_name || "Unassigned"}</td>
                              <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">{new Date(request.created_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                  <PaginationControls current={hrCurrentPage} total={hrTotalPages} onPageChange={setHrCurrentPage} testIdPrefix="hr-history" />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
